import { baseApi as api } from './baseApi';

/** Llamadas del chat (fase 2). Ver chatCalls.service.ts del backend. */
export type ChatCallStatus = 'ringing' | 'ongoing' | 'ended' | 'missed' | 'rejected' | 'cancelled';
export type ChatCallParticipantStatus = 'ringing' | 'joined' | 'left' | 'rejected' | 'missed';

export interface ChatCall {
  _id: string;
  conversationId: string;
  kind: 'direct' | 'group';
  title: string;
  from: string;
  fromName: string;
  status: ChatCallStatus;
  participants: { user: string; name: string; status: ChatCallParticipantStatus }[];
  expiresAt: string;
  answeredAt: string | null;
  endedAt: string | null;
}

export const LLAMADA_ACTIVA = { type: 'ChatCall' as const, id: 'ACTIVE' };

export const chatCallsApi = api.injectEndpoints({
  endpoints: (build) => ({
    /** La llamada en la que estoy adentro (sonando para los demás o en curso). */
    getActiveChatCall: build.query<ChatCall | null, void>({
      query: () => '/chat-calls/active',
      transformResponse: (r: { data: ChatCall | null }) => r.data,
      providesTags: [LLAMADA_ACTIVA],
    }),
    getChatCall: build.query<ChatCall, string>({
      query: (id) => `/chat-calls/${id}`,
      transformResponse: (r: { data: ChatCall }) => r.data,
      providesTags: (_r, _e, id) => [{ type: 'ChatCall', id }],
    }),
    /** Fase 3: el pase a la sala de audio/video (LiveKit). Mutación para pedirlo cada vez, sin caché. */
    getChatCallToken: build.mutation<{ url: string; token: string }, string>({
      query: (callId) => ({ url: `/chat-calls/${callId}/token`, method: 'GET' }),
      transformResponse: (r: { data: { url: string; token: string } }) => r.data,
    }),
    startChatCall: build.mutation<ChatCall, string>({
      query: (conversationId) => ({ url: `/chat-calls/conversations/${conversationId}`, method: 'POST' }),
      transformResponse: (r: { data: ChatCall }) => r.data,
      invalidatesTags: (r) => [LLAMADA_ACTIVA, ...(r ? [{ type: 'ChatCall' as const, id: r._id }] : [])],
    }),
    respondChatCall: build.mutation<ChatCall, { callId: string; acepta: boolean }>({
      query: ({ callId, acepta }) => ({ url: `/chat-calls/${callId}/respond`, method: 'POST', body: { acepta } }),
      transformResponse: (r: { data: ChatCall }) => r.data,
      invalidatesTags: (_r, _e, { callId }) => [LLAMADA_ACTIVA, { type: 'ChatCall', id: callId }],
    }),
    hangupChatCall: build.mutation<ChatCall, string>({
      query: (callId) => ({ url: `/chat-calls/${callId}/hangup`, method: 'POST' }),
      transformResponse: (r: { data: ChatCall }) => r.data,
      invalidatesTags: (_r, _e, callId) => [LLAMADA_ACTIVA, { type: 'ChatCall', id: callId }],
    }),
  }),
});

export const {
  useGetActiveChatCallQuery, useGetChatCallQuery, useGetChatCallTokenMutation, useStartChatCallMutation, useRespondChatCallMutation, useHangupChatCallMutation,
} = chatCallsApi;
